<template>
  <div
      class="feed-component"
      :class="{
        empty: isEmptyFeed,
        loading: isLoading
      }"
      ref="feed"
  >
    <div v-if="isLoading" class="feed-loader"></div>
    <template v-else>
      <div v-if="isEmptyFeed">
        Событий нет
      </div>
      <template v-else>
        <template v-for="(day, index) in feedByDays" :key="'day_header' + index">
          <header>
            <span>{{ day.label }}</span>
          </header>

          <FeedItem
              v-for="(record, index) in day.data"
              :key="'feed-item_' + index"
              :record="record"
          />

        </template>
      </template>
    </template>

    <VButton
        v-if="showScrollToBottom"
        class="scroll-btn"
        @click="scrollToEndFeed(true, true)"
    >
      <div class="icon" />
    </VButton>

  </div>
</template>

<style lang="scss" src="./Feed.scss"/>

<script lang="ts">
import EntityPointer           from '@/domains/EntityPointer/EntityPointer';
import FeedRecord              from '@/core/services/Feeds/FeedRecord';
import FeedsService            from '@/core/services/Feeds/FeedsService';
import FeedItem                from './FeedItem.vue';

export default defineComponent({
  name: 'Feed',

  components: {
    FeedItem,
  },

  props: {
    /**
     * Родитель(и) фида. Один указатель либо массив — feeds.get на беке
     * штатно принимает несколько сущностей (например supplyOffers
     * к записям партнёра). Резолвится к массиву внутри.
     *
     * Не обязателен, если записи переданы готовыми через records.
     */
    parentEntities: {
      type:    [Object, Array] as PropType<EntityPointer | EntityPointer[]>,
      default: null,
    },

    /** Фильтр по типам записей: 'comment' | 'changelog' | 'notification'. */
    feedTypes: {
      type:    Array as PropType<string[]>,
      default: () => [],
    },

    /**
     * Готовые записи извне. Если переданы — Feed их показывает и НЕ грузит сам
     * через feeds.get. Нужно для случаев, где источник истории не feeds.get
     * (история товара произв. плана грузится через productionPlans.getProductLogs).
     */
    records: {
      type:    Array as PropType<FeedRecord[]>,
      default: null,
    },

    /**
     * Публичный (маскированный) фид вместо приватного.
     * null (по умолчанию) — бэк решает по роли (покупателю приватный, остальным публичный).
     * Задавать явно только если нужно переопределить — обычно не требуется.
     */
    isPublic: {
      type:    Boolean as PropType<boolean | null>,
      default: null,
    },
  },

  data() {
    return {
      feed: [] as FeedRecord[],
      isLoading: true,

      // id уже показанных записей — для пометки новизны при дельта-дозагрузке (pushRecords)
      seenIds: new Set<number>(),

      feedContainerTouched: false,
      showScrollToBottom: false,
    };
  },

  computed: {
    /** Источник записей задан снаружи — Feed не грузит сам. */
    hasExternalRecords(): boolean {
      return this.records !== null;
    },

    /** Нормализует входной проп к массиву указателей. */
    parentEntitiesList(): EntityPointer[] {
      if (this.parentEntities === null) {
        return [];
      }
      else if (Array.isArray(this.parentEntities)) {
        return this.parentEntities;
      }
      else {
        return [this.parentEntities];
      }
    },

    /**
     * Ключ родителей для сравнения по значению, а не по ссылке.
     *
     * Потребители отдают указатель из computed (`new EntityPointer(...)`), и он
     * пересоздаётся при любом переприсваивании модели: сущность та же, ссылка
     * новая. Вотчер по самому parentEntitiesList срабатывал на этом и грузил фид
     * второй раз — поверх явного renewData() от родителя.
     */
    parentEntitiesKey(): string {
      return this.parentEntitiesList
        .map(_pointer => `${_pointer.typeName}:${_pointer.id}`)
        .join(',');
    },

    isEmptyFeed(): boolean {
      return this.$core.utils.isEmptyArray(this.feed);
    },

    feedByDays() {
      const daysMap = new Map<number, {
        time: number;
        label: string;
        data: FeedRecord[];
      }>();

      for (const record of this.feed) {
        // record.time — TimeStamp (extends Date), готов к работе без нормализации
        const dayStart = this.$core.utils.date.toDayStart(record.time);
        const dayTimestamp = dayStart.getTime();

        if (!daysMap.has(dayTimestamp)) {
          const label = this.formatDayLabel(dayStart, dayTimestamp);
          daysMap.set(dayTimestamp, {
            time: dayTimestamp,
            label,
            data: [],
          });
        }

        daysMap.get(dayTimestamp)!.data.push(record);
      }

      return Array.from(daysMap.values());
    },
  },

  watch: {
    // Сменился родитель фида — перезагружаем. Сравнение по ключу (typeName:id),
    // а не по ссылке на указатель: см. parentEntitiesKey.
    parentEntitiesKey() {
      if (!this.hasExternalRecords) {
        this.renewData();
      }
    },

    records: {
      immediate: true,
      handler() {
        if (this.hasExternalRecords) {
          this.feed = this.records;
          this.isLoading = false;
          this.scrollToEndFeed(true, false);
        }
      },
    },
  },

  mounted() {
    if (!this.hasExternalRecords) {
      this.renewData();
    }

    const feedEl = this.$refs.feed as HTMLElement;
    feedEl?.addEventListener('scroll', this.onFeedContainerScroll, { passive: true });
  },

  beforeUnmount() {
    const feedEl = this.$refs.feed as HTMLElement;
    feedEl?.removeEventListener('scroll', this.onFeedContainerScroll);
  },

  methods: {
    /**
     * Перезагрузка фида через feeds.get. Вызывается родителем по ref после
     * создания комментария — напрямую, без глобальной шины: фид всегда отрисован
     * под конкретной сущностью. При внешних records загрузка не делается.
     */
    async renewData() {
      if (this.hasExternalRecords) {
        return;
      }

      this.isLoading = true;

      try {
        this.feed = await FeedsService.get(
          this.parentEntitiesList,
          this.feedTypes,
          undefined,
          this.isPublic ?? undefined,
        );
        this.rememberSeen(this.feed);
        this.scrollToEndFeed(true, false);
      }
      finally {
        this.isLoading = false;
      }
    },

    /**
     * Дельта-дозагрузка: подмешивает новые записи к показанным.
     * Записи, чьих id ещё не видели, помечаются isNew (блинк); дубли отбрасываются.
     * Первая порция (seenIds пуст) — тихая, без блинка: открытая лента не должна мигать целиком.
     * Polling (интервал, жизненный цикл) — на стороне вызывающего; Feed только подмешивает.
     */
    pushRecords(records: FeedRecord[]): number {
      const isFirstBatch = this.seenIds.size === 0;
      let newCount = 0;
      for (const record of records) {
        if (this.seenIds.has(record.payload.id)) {
          continue;
        }

        if (isFirstBatch === false) {
          record.isNew = true;
        }

        this.seenIds.add(record.payload.id);
        this.feed.push(record);

        newCount++;
      }

      this.scrollToEndFeed(false, true);

      return newCount;
    },

    /** Запоминает id показанных записей (для дальнейшей пометки новизны). */
    rememberSeen(records: FeedRecord[]) {
      for (const record of records) {
        this.seenIds.add(record.payload.id);
      }
    },

    /** Загруженные записи — для потребителей, которым нужно их выгрузить (экспорт). */
    getRecords(): FeedRecord[] {
      return this.feed;
    },

    formatDayLabel(date: Date, timestamp: number): string {
      const humanLabel = this.$core.utils.date.humanizeDate(date, null);

      const relativeLabels = ['Послезавтра', 'Завтра', 'Сегодня', 'Вчера', 'Позавчера'];

      if (relativeLabels.includes(humanLabel)) {
        return `${humanLabel} (${this.$core.utils.date.format(timestamp, 'MMMM|D')})`;
      }

      return humanLabel;
    },

    onFeedContainerScroll() {
      const feedEl = this.$refs.feed as HTMLElement | null;
      if (!feedEl) return;

      const atBottomThreshold = 10;
      const diff = feedEl.scrollHeight - feedEl.scrollTop - feedEl.clientHeight;

      const isAtBottom = diff <= atBottomThreshold;

      this.showScrollToBottom = !isAtBottom && feedEl.scrollHeight > feedEl.clientHeight;

      this.feedContainerTouched = !isAtBottom;
    },

    scrollToEndFeed(isForce = false, isSmooth = false) {
      const feedEl = this.$refs.feed as HTMLElement | null;
      if (!feedEl) return;

      if (this.feedContainerTouched && !isForce) return;

      this.$nextTick().then(async () => {
        await this.$nextTick();
        feedEl.scrollTo({
          top: feedEl.scrollHeight,
          behavior: isSmooth ? 'smooth' : 'auto',
        });

        this.feedContainerTouched = false;
        this.showScrollToBottom = false;
      });
    },
  },
});
</script>
